import { addressLine } from './address-words'
import { isLocalOnly } from './api'
import { getMe } from './me'
import { setTimesOn, visibleTime } from './business-settings'
import type { TodayClean, TodayPayload } from './types'
import { addDaysISO, el, toast, todayISO } from './ui'
import { roundConfig, shownTime } from './round-config'

// Job reminders on this phone (migration 0026, 2026-09-25). Turned on from the
// Today screen: the phone is asked for permission, gives Roundly a push
// address, and roundly-worker then sends "Next job at 10:30: Anna, 17 Crescent
// Rise" 15 minutes before each of this person's jobs that has a time.
//
// iPhones only allow it once Roundly is on the Home Screen (Share, then Add to
// Home Screen), opened from there. The switch says so instead of failing.
//
// Inside Roundly's iPhone app (ios/, 2026-09-25) Apple gives the page no push
// at all, so the phone keeps the reminders itself (ios/Roundly/JobReminders.swift):
// the page hands it this person's jobs with a time for today and the next two
// days, the same list their Today shows, and hands it again when the app
// comes back on screen and whenever the round changes on this phone.

// Roundly's public reminder key (the private half is a secret on
// roundly-worker). Public by design: phones need it to accept the messages.
const VAPID_PUBLIC_KEY = 'BAQHDdd14Acc8Mt6xM8xA9Zw03GAkklxUnRl4fv0NwPVvIbxzBvHFkR7svZALPWc8ev0rGvQdAeI5pf-hwFiymU'

type Support = 'app' | 'yes' | 'iphone-home-screen' | 'no'

// window.RoundlyIOS, added by the iPhone app before the page starts.
interface RoundlyApp {
  call(method: string, params?: unknown): Promise<unknown>
}

function roundlyApp(): RoundlyApp | null {
  if (typeof window === 'undefined') return null
  const app = (window as unknown as { RoundlyIOS?: RoundlyApp }).RoundlyIOS
  return app && typeof app.call === 'function' ? app : null
}

interface AppReminderStatus {
  on?: boolean
  permission?: 'granted' | 'denied' | 'ask'
}

async function appCall(method: string, params?: unknown): Promise<AppReminderStatus | null> {
  const app = roundlyApp()
  if (!app) return null
  try {
    const answer = await app.call(method, params)
    return answer && typeof answer === 'object' ? (answer as AppReminderStatus) : {}
  } catch {
    return null
  }
}

export function reminderSupport(): Support {
  if (roundlyApp()) return 'app'
  const hasPush = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (hasPush) return 'yes'
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return 'iphone-home-screen'
  return 'no'
}

function keyBytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4)
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/')
  return existing ?? navigator.serviceWorker.register('/sw.js', { scope: '/' })
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (reminderSupport() !== 'yes') return null
  const reg = await navigator.serviceWorker.getRegistration('/')
  return reg ? reg.pushManager.getSubscription() : null
}

// The business this phone is signed in to, as every request says it.
function businessHeader(): Record<string, string> {
  const id = getMe()?.businessId
  return typeof id === 'number' ? { 'X-Roundly-Business': String(id) } : {}
}

// What the server keeps for this phone (0057): job reminders, enquiry alerts, or both; kept: the phone still needs its
// push address for the other one, so turning one off must not let go of it.
interface PushAnswer { kept?: boolean; reminders?: boolean; enquiries?: boolean; error?: string }
async function pushCall(method: 'POST' | 'DELETE', body: unknown): Promise<PushAnswer | null> {
  // Opened with no signal: nothing is sent until the server confirms who this is (docs/plans/offline/PLAN-v2.md §2).
  if (isLocalOnly()) return null
  try {
    const res = await fetch('/api/push', {
      method,
      headers: { 'content-type': 'application/json', 'Idempotency-Key': crypto.randomUUID(), ...businessHeader() },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: AbortSignal.timeout(15_000),
    })
    if (!res.ok) return null
    return (await res.json().catch(() => ({}))) as PushAnswer
  } catch {
    return null
  }
}
async function tell(method: 'POST' | 'DELETE', body: unknown): Promise<boolean> {
  return (await pushCall(method, body)) !== null
}

// What this phone has on (GET /api/push): null when the phone has no push address here, or the server could not be
// asked (then the phone's own answer stands).
export async function phoneAlerts(): Promise<{ reminders: boolean; enquiries: boolean } | null> {
  if (isLocalOnly()) return null
  const sub = await currentSubscription().catch(() => null)
  if (!sub) return null
  try {
    const res = await fetch(`/api/push?endpoint=${encodeURIComponent(sub.endpoint)}`, { headers: businessHeader(), credentials: 'same-origin', signal: AbortSignal.timeout(15_000) })
    if (!res.ok) return null
    const a = (await res.json()) as PushAnswer
    return { reminders: a.reminders === true, enquiries: a.enquiries === true }
  } catch {
    return null
  }
}

export async function remindersAreOn(): Promise<boolean> {
  if (roundlyApp()) return (await appCall('reminders.status'))?.on === true
  try {
    if ((await currentSubscription()) === null || Notification.permission !== 'granted') return false
    // The same phone address may be there for enquiry alerts only (0057): the server says which.
    const has = await phoneAlerts()
    return has ? has.reminders : true
  } catch {
    return false
  }
}

export async function turnRemindersOn(): Promise<string | null> {
  if (roundlyApp()) {
    const status = await appCall('reminders.enable')
    if (status?.on) {
      await syncAppReminders()
      return null
    }
    if (status?.permission === 'denied') return "Notifications are off for Roundly. Turn them on in the phone's Settings, under Notifications, then try again."
    return "This phone wouldn't turn reminders on. Try again."
  }
  try {
    return await subscribeHere()
  } catch {
    // The phone or its push service said no (a blocked browser, a private
    // window): say so rather than leave the button looking dead.
    return "This phone wouldn't turn reminders on. Check notifications are allowed for Roundly in its settings."
  }
}

async function subscribeHere(kind: 'reminders' | 'enquiries' = 'reminders'): Promise<string | null> {
  if (reminderSupport() !== 'yes') return 'This phone cannot show reminders from Roundly.'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return "Notifications are off for Roundly. Allow them in the phone's settings, then try again."
  const reg = await registration()
  await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(VAPID_PUBLIC_KEY) as BufferSource })
  const json = sub.toJSON()
  const answer = await pushCall('POST', { endpoint: json.endpoint, keys: json.keys, ...(kind === 'enquiries' ? { for: 'enquiries' } : {}) })
  if (!answer) {
    // Let go of the address only when nothing else on this phone uses it.
    const still = await phoneAlerts()
    if (!still || (!still.reminders && !still.enquiries)) await sub.unsubscribe().catch(() => undefined)
    return kind === 'enquiries' ? "Couldn't turn alerts on. Check your signal and try again." : "Couldn't turn reminders on. Check your signal and try again."
  }
  return null
}

// The Roundly iPhone app's own alerts for new enquiries (10 Oct 2026; ios/Roundly/EnquiryAlerts.swift): Apple gives the
// app an address, sent to Roundly as "apns:<token>". An app from before this answers "Not available" and has none.
interface AppPush { permission?: 'granted' | 'denied' | 'ask'; token?: string; sandbox?: boolean }
let appPushKnown: boolean | null = null
export async function appHasPush(): Promise<boolean> {
  const app = roundlyApp()
  if (!app) return false
  if (appPushKnown !== null) return appPushKnown
  try { appPushKnown = ((await app.call('app.info')) as { push?: boolean } | null)?.push === true } catch { appPushKnown = false }
  return appPushKnown
}
async function appPush(method: 'push.register' | 'push.status'): Promise<AppPush | null> {
  const app = roundlyApp()
  if (!app) return null
  try { return ((await app.call(method)) ?? {}) as AppPush } catch { return null }
}
const appAddress = (p: AppPush | null): string | null =>
  p?.token && /^[0-9a-f]{64,200}$/.test(p.token) ? `${p.sandbox ? 'apns-sandbox' : 'apns'}:${p.token}` : null

// Enquiry alerts on this phone (New jobs; owners; 0057): the same phone address as job reminders, its own switch.
// In the Roundly iPhone app: the app's own address from Apple.
export async function enquiryAlertsOn(): Promise<boolean> {
  if (roundlyApp()) {
    if (!(await appHasPush()) || isLocalOnly()) return false
    const address = appAddress(await appPush('push.status'))
    if (!address) return false
    try {
      const res = await fetch(`/api/push?endpoint=${encodeURIComponent(address)}`, { headers: businessHeader(), credentials: 'same-origin', signal: AbortSignal.timeout(15_000) })
      return res.ok && ((await res.json()) as PushAnswer).enquiries === true
    } catch {
      return false
    }
  }
  if (reminderSupport() !== 'yes') return false
  try {
    if (Notification.permission !== 'granted') return false
    return (await phoneAlerts())?.enquiries === true
  } catch {
    return false
  }
}
export async function turnEnquiryAlertsOn(): Promise<string | null> {
  if (roundlyApp()) {
    const answer = await appPush('push.register')
    if (answer?.permission === 'denied') return "Notifications are off for Roundly. Turn them on in the phone's Settings, under Notifications, then try again."
    const address = appAddress(answer)
    if (!address) return "This phone wouldn't turn alerts on. Check your signal and try again."
    return (await pushCall('POST', { endpoint: address, for: 'enquiries' })) ? null : "Couldn't turn alerts on. Check your signal and try again."
  }
  try {
    return await subscribeHere('enquiries')
  } catch {
    return "This phone wouldn't turn alerts on. Check notifications are allowed for Roundly in its settings."
  }
}
export async function turnEnquiryAlertsOff(): Promise<boolean> {
  if (roundlyApp()) {
    const address = appAddress(await appPush('push.status'))
    return address ? (await pushCall('DELETE', { endpoint: address, for: 'enquiries' })) !== null : true
  }
  const sub = await currentSubscription().catch(() => null)
  if (!sub) return true
  const answer = await pushCall('DELETE', { endpoint: sub.endpoint, for: 'enquiries' })
  if (answer && !answer.kept) await sub.unsubscribe().catch(() => undefined)
  return answer !== null
}

// After Log out: the server has already dropped this phone's reminders with
// the sign-in; the phone lets go of its address too.
export async function forgetRemindersHere(): Promise<void> {
  // In the app: every waiting reminder goes, and any showing on the lock
  // screen, so no customer's name is left behind for the next person.
  if (roundlyApp()) {
    await appCall('reminders.disable')
    return
  }
  const sub = await currentSubscription().catch(() => null)
  await sub?.unsubscribe().catch(() => undefined)
}

// Off on this phone: the server forgets the address and the phone lets go of
// it.
export async function turnRemindersOff(): Promise<boolean> {
  if (roundlyApp()) return (await appCall('reminders.disable')) !== null
  const sub = await currentSubscription().catch(() => null)
  if (!sub) return true
  const answer = await pushCall('DELETE', { endpoint: sub.endpoint })
  // Enquiry alerts still on for this phone (0057): the server kept its address, so the phone keeps it too.
  if (!answer?.kept) await sub.unsubscribe().catch(() => undefined)
  return answer !== null
}

// In the app: the reminder for each of this person's jobs with a time, today
// and the next two days. Only jobs still to do: not started, and not marked
// "couldn't do it". The words match the ones roundly-worker sends to a Home
// Screen Roundly (worker/src/reminders.ts reminderMessage).
//
// Two or more jobs at one house at the same time (several jobs) are one reminder naming each, gutters first, as
// roundly-worker sends them (worker/src/reminders.ts groupReminders, jobsLine).
const KIND_WORD: Record<string, string> = { gutter: 'Gutters', other: 'Other service', window: 'Windows' }
const KIND_ORDER: Record<string, number> = { gutter: 0, other: 1, window: 2 }
// Only a round with several jobs switched on groups (`severalJobs`); off, one reminder per visit as before.
export function appReminderJobs(cleans: TodayClean[], severalJobs = false): { id: number; date: string; time: string; title: string; body: string }[] {
  const out: { id: number; date: string; time: string; title: string; body: string }[] = []
  const houses = new Map<string, TodayClean[]>()
  for (const c of cleans) {
    // A round with no set times sends none (visibleTime is empty), as roundly-worker's DUE_SQL skips it.
    const time = visibleTime(c)
    if (c.state !== 'booked' || !time || c.started_at || c.blocked_at || c.blocked_reason) continue
    const k = severalJobs ? `${c.customer_id}|${c.date}|${time}` : `visit|${c.id}`
    const same = houses.get(k)
    if (same) { same.push(c); continue }
    houses.set(k, [c])
  }
  for (const group of houses.values()) {
    const c = group[0]!
    const time = visibleTime(c)!
    const where = addressLine(c.address, c.postcode)
    const words = [...group].sort((a, b) => (KIND_ORDER[a.kind ?? 'window'] ?? 1) - (KIND_ORDER[b.kind ?? 'window'] ?? 1) || a.id - b.id)
      .map((v) => v.job_name?.trim() || KIND_WORD[v.kind ?? 'window'] || 'Windows')
    const jobs = words.length > 1 ? `. ${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : ''
    out.push({ id: c.id, date: c.date, time, title: `Next job at ${shownTime(time)}`, body: [c.customer_name || 'A customer', where].filter(Boolean).join(', ') + jobs })
  }
  return out
}

async function syncAppReminders(): Promise<void> {
  // Opened with no signal: the phone keeps the reminders it has; they are brought up to date once signal is back.
  if (isLocalOnly()) return
  const me = getMe()
  const name = me?.workerName?.trim()
  if (!roundlyApp() || !name || (me?.role !== 'worker' && me?.role !== 'owner')) return
  if ((await appCall('reminders.status'))?.on !== true) return
  const from = todayISO()
  const to = addDaysISO(from, 2)
  // A worker's list is theirs by sign-in; an owner asks for the name on their
  // own jobs, exactly as their Today tab does.
  const who = me.role === 'owner' ? `&worker=${encodeURIComponent(name)}` : ''
  let data: TodayPayload
  try {
    const res = await fetch(`/api/today?from=${from}&to=${to}${who}`, {
      headers: businessHeader(),
      credentials: 'same-origin',
      signal: AbortSignal.timeout(15_000),
    })
    // Signed out here or from another phone, removed from the round, or the
    // round paused: every reminder goes, as the Home Screen ones stop on the
    // server (security review, 2026-09-25).
    if (res.status === 401 || res.status === 403) {
      await forgetRemindersHere()
      return
    }
    // No signal, or the server failed: the phone keeps the reminders it has.
    if (!res.ok) return
    data = (await res.json()) as TodayPayload
  } catch {
    return
  }
  if (!Array.isArray(data?.cleans)) return
  // The jobs' times are on the round's own clock (Step 2 round 5). An app from before can only read London's: a round
  // anywhere else gets no reminders from it (none at the wrong time), and Today says to update the app.
  // The signed-in round's own clock (from /api/me, so a switch to another round never sends the last one's).
  const timeZone = me.round?.timeZone || roundConfig().timeZone
  if (!(await appReadsRoundClock(timeZone))) {
    await appCall('reminders.set', { jobs: [] })
    return
  }
  await appCall('reminders.set', { jobs: appReminderJobs(data.cleans, getMe()?.severalJobs === true), timeZone })
}

// Whether this iPhone app reads job times on the round's own clock (app.info roundClock, Step 2 round 5). A London
// round needs nothing new: every app reads London.
let roundClockKnown: boolean | null = null
export async function appReadsRoundClock(timeZone: string = roundConfig().timeZone): Promise<boolean> {
  if (timeZone === 'Europe/London') return true
  const app = roundlyApp()
  if (!app) return false
  if (roundClockKnown !== null) return roundClockKnown
  // A failed ask is not kept: the next one asks again (peer review, round 5).
  try { roundClockKnown = ((await app.call('app.info')) as { roundClock?: boolean } | null)?.roundClock === true } catch { return false }
  return roundClockKnown
}

// The iPhone app only: every reminder goes, and the switch turns off. For the
// start-up screens where nobody may have them (signed out, removed from the
// round, round paused). Outside the app it does nothing: the server already
// stops Home Screen reminders in all three cases.
export async function forgetAppReminders(): Promise<void> {
  if (roundlyApp()) await appCall('reminders.disable')
}

// Once, after sign-in (main.ts): keep the app's reminders in step with the
// round. Sent now, when the app comes back on screen, and a few seconds after
// anything changes on this phone.
let watching = false
export function watchAppReminders(): void {
  if (watching || !roundlyApp()) return
  watching = true
  let timer: number | undefined
  let waiting = false
  const soon = (): void => {
    waiting = true
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      waiting = false
      void syncAppReminders()
    }, 3000)
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void syncAppReminders()
    // A change made just before the phone is locked is sent now, not left
    // waiting on a timer that stops with the screen.
    else if (waiting) {
      waiting = false
      window.clearTimeout(timer)
      void syncAppReminders()
    }
  })
  window.addEventListener('dropby:data-changed', soon)
  void syncAppReminders()
}

// The row at the foot of Today, for anyone with jobs of their own.
export function remindersRow(): HTMLElement | null {
  const me = getMe()
  if (!me?.workerName?.trim() || (me.role !== 'worker' && me.role !== 'owner')) return null
  const row = el('section', { class: 'reminders-row', 'aria-label': 'Job reminders' })
  const words = el('div', { class: 'reminders-words' }, el('strong', {}, 'Job reminders'))
  const note = el('span', { class: 'reminders-note' })
  words.append(note)
  const button = el('button', { type: 'button', class: 'btn-secondary reminders-button' })
  row.append(words, button)

  // A round with no set times (Your business) has no time to buzz before, so no reminders are sent: said, not offered.
  if (!setTimesOn()) {
    note.textContent = "Off for the whole round: your business doesn't give customers set times, so there is no time to remind you before."
    button.remove()
    return row
  }
  const support = reminderSupport()
  if (support === 'iphone-home-screen') {
    note.textContent = 'On iPhone, add Roundly to your Home Screen first: tap Share, then Add to Home Screen. Open it from there to turn reminders on.'
    button.remove()
    return row
  }
  if (support === 'no') {
    note.textContent = "This browser can't show reminders. Try Chrome or Safari on your phone."
    button.remove()
    return row
  }

  let needsUpdate = false
  const show = (on: boolean): void => {
    if (needsUpdate) return
    note.textContent = on
      ? support === 'app'
        ? 'On. Your phone buzzes 15 minutes before each job with a time, using your jobs as they were when you last opened Roundly.'
        : 'On. Your phone buzzes 15 minutes before each job with a time.'
      : 'Get a buzz on this phone 15 minutes before each job with a time.'
    button.textContent = on ? 'Turn off' : 'Turn on'
    button.setAttribute('aria-pressed', String(on))
  }
  show(false)
  // An app from before round 5 on a round outside London: said, not offered (its reminders would come at London's time).
  if (support === 'app') void appReadsRoundClock().then((ok) => {
    if (ok || !row.isConnected) return
    needsUpdate = true
    note.textContent = 'Update the Roundly app to get reminders at your own time: open TestFlight and tap Update.'
    button.remove()
  })
  void remindersAreOn().then((on) => {
    show(on)
    // Reminders follow whoever is signed in on this phone now: said again
    // quietly each time Today opens, so they never belong to an older sign-in.
    // (In the app the phone holds them itself: watchAppReminders.)
    if (on && support === 'yes') void currentSubscription().then((sub) => {
      const json = sub?.toJSON()
      if (json?.endpoint) void tell('POST', { endpoint: json.endpoint, keys: json.keys })
    })
  })
  button.addEventListener('click', () => {
    const on = button.getAttribute('aria-pressed') === 'true'
    button.disabled = true
    void (on ? turnRemindersOff().then((ok) => (ok ? null : "Couldn't turn reminders off. Try again with signal.")) : turnRemindersOn())
      .then((problem) => {
        if (problem) toast(problem)
        else toast(on ? 'Reminders off on this phone.' : "Reminders on. You'll get a buzz 15 minutes before each job.")
        return remindersAreOn()
      })
      .then(show)
      .finally(() => {
        button.disabled = false
      })
  })
  return row
}
